import { z } from 'zod';
import { localeSchema } from './schemas.js';
// Fotos de les propietats (§3, §4, §20.5). Es redimensionen al navegador de l'agent i es pugen al
// bucket públic. Cada foto té diverses mides (variants) amb nom versionat i memòria cau d'un any.
/** Amplades que es generen, en píxels. Mai més grans que l'original. */
export const MEDIA_WIDTHS = [640, 1280, 2048];
export const MEDIA_MAX_BYTES = 8 * 1024 * 1024;
export const MEDIA_CONTENT_TYPES = ['image/webp', 'image/jpeg'];
export const MEDIA_CACHE_CONTROL = 'public, max-age=31536000, immutable';
export const MEDIA_MAX_PER_PROPERTY = 80;
export const MEDIA_ROOMS = [
    'exterior', 'livingRoom', 'diningRoom', 'kitchen', 'bedroom', 'bathroom',
    'terrace', 'garden', 'pool', 'views', 'other',
];
export const mediaRoomSchema = z.enum(MEDIA_ROOMS);
const ID_RE = /^[A-Za-z0-9]{20}$/;
export const mediaIdSchema = z.string().regex(ID_RE);
/** Identificador d'una foto: 20 caràcters, com els de Firestore. Es genera al navegador. */
export function newMediaId(random = Math.random) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({ length: 20 }, () => chars[Math.floor(random() * chars.length)]).join('');
}
/**
 * Camí d'una variant al bucket: `photos/{propietat}/{foto}_{timestamp}_{amplada}.{ext}`.
 * El timestamp el fa únic: amb memòria cau d'un any, substituir una foto ha de canviar-ne el nom (§20.5).
 * Les regles de Storage exigeixen exactament aquest format.
 */
export function mediaPath(propertyId, mediaId, timestamp, width, contentType) {
    return `photos/${propertyId}/${mediaId}_${timestamp}_${width}.${contentType === 'image/webp' ? 'webp' : 'jpg'}`;
}
/** Comprova que un camí és d'aquella propietat i d'aquella foto, amb el format esperat. */
export function isMediaPathOf(path, propertyId, mediaId) {
    const prefix = `photos/${propertyId}/${mediaId}_`;
    return path.startsWith(prefix) && /^\d{13}_\d{2,4}\.(webp|jpg)$/.test(path.slice(prefix.length));
}
/**
 * Imatge per a la vista prèvia dels enllaços (WhatsApp, xarxes, correu): JPEG de 1200 × 630,
 * retallada, i de menys de 300 kB, perquè alguns serveis no accepten WebP i descarten les grans sense avisar.
 */
export const MEDIA_OG = { width: 1200, height: 630, maxBytes: 300 * 1024 };
export function mediaOgPath(propertyId, mediaId, timestamp) {
    return `photos/${propertyId}/${mediaId}_${timestamp}_og.jpg`;
}
/** Comprova la imatge de vista prèvia: camí d'aquella foto i timestamp, JPEG i mida exacta. */
export function isValidOg(og, propertyId, mediaId, timestamp) {
    return og.path === mediaOgPath(propertyId, mediaId, timestamp)
        && og.contentType === 'image/jpeg'
        && og.width === MEDIA_OG.width
        && og.height === MEDIA_OG.height
        && og.bytes <= MEDIA_OG.maxBytes;
}
/** Les mides que toca generar per a una imatge d'aquesta amplada: almenys una, sense ampliar. */
export function variantWidths(originalWidth) {
    const widths = MEDIA_WIDTHS.filter((w) => w < originalWidth);
    return widths.length === 0 || originalWidth <= MEDIA_WIDTHS[MEDIA_WIDTHS.length - 1] ? [...widths, Math.min(originalWidth, MEDIA_WIDTHS[MEDIA_WIDTHS.length - 1])] : widths;
}
export const mediaVariantSchema = z.object({
    path: z.string().min(1).max(300),
    width: z.number().int().min(1).max(8000),
    height: z.number().int().min(1).max(8000),
    contentType: z.enum(MEDIA_CONTENT_TYPES),
    bytes: z.number().int().min(1).max(MEDIA_MAX_BYTES),
});
export const addPropertyMediaInputSchema = z.object({
    propertyId: z.string().min(1).max(128),
    mediaId: mediaIdSchema,
    variants: z.array(mediaVariantSchema).min(1).max(MEDIA_WIDTHS.length),
    og: mediaVariantSchema.optional(),
});
/**
 * Peu destacat d'una foto (§10.2), com a molt un per foto:
 * - `text`: text lliure i traduïble (§11.2), curt;
 * - `priceFrom`: el preu «des de», calculat amb les tarifes, mai escrit a mà;
 * - `distance`: una distància de la fitxa (grup Entorn), p. ex. «Platja · 3,5 km».
 */
export const CAPTION_TEXT_MAX = 60;
export const captionInputSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text'), text: z.string().trim().min(1).max(CAPTION_TEXT_MAX) }),
    z.object({ kind: z.literal('priceFrom') }),
    z.object({ kind: z.literal('distance'), specKey: z.string().min(1).max(60) }),
]);
export const updatePropertyMediaInputSchema = z.object({
    propertyId: z.string().min(1).max(128),
    locale: localeSchema,
    /** Ordre complet de la galeria (tots els ids). */
    order: z.array(mediaIdSchema).max(MEDIA_MAX_PER_PROPERTY),
    coverId: mediaIdSchema.nullable(),
    changes: z.record(mediaIdSchema, z.object({
        alt: z.string().trim().max(200).optional(),
        room: mediaRoomSchema.nullable().optional(),
        showInHome: z.boolean().optional(),
        showInSlider: z.boolean().optional(),
        /** null: treu el peu. Un `text` s'escriu en l'idioma de l'app (`locale`). */
        caption: captionInputSchema.nullable().optional(),
    })),
});
export const deletePropertyMediaInputSchema = z.object({
    propertyId: z.string().min(1).max(128),
    mediaId: mediaIdSchema,
});
/** La variant més petita que fa almenys `minWidth` px (o la més gran, si cap no hi arriba). */
export function pickVariant(variants, minWidth) {
    const sorted = [...variants].sort((a, b) => a.width - b.width);
    return sorted.find((v) => v.width >= minWidth) ?? sorted[sorted.length - 1];
}
/**
 * Comprova un conjunt de variants abans de registrar-lo: camins d'aquella foto, un sol timestamp,
 * amplades diferents. Retorna el timestamp, o null si no és vàlid.
 */
export function checkVariants(propertyId, mediaId, variants) {
    const stamps = new Set();
    const widths = new Set();
    for (const v of variants) {
        if (!isMediaPathOf(v.path, propertyId, mediaId))
            return null;
        const [, stamp, rest] = v.path.slice(`photos/${propertyId}/`.length).split('_');
        const [width, ext] = (rest ?? '').split('.');
        if (Number(width) !== v.width)
            return null;
        if ((ext === 'webp') !== (v.contentType === 'image/webp'))
            return null;
        stamps.add(stamp ?? '');
        widths.add(v.width);
    }
    if (stamps.size !== 1 || widths.size !== variants.length)
        return null;
    return Number([...stamps][0]);
}
